import uuid from "uuid"
import { currencyNameToSymbol as _currencyNameToSymbol } from "./currency"
import { isBlank } from "@cd2/components"
import * as dateFns from "date-fns"
import { IAnalysisCodes, IRecord } from "../types/schema"
import { IDeletedItem, IItem, isDeleted, itemTypes } from "../stories/quoteOrders/quoteOrderUtils"

interface ITruncateOptions {
  omission?: string
  length?: number
  separator?: string
}
/**
 * Truncate duplicated from @cd2/tools, so that it can be used in server side methods
 * as the @cd2/tools cannot be imported on the server
 */
export function truncate(text: string, options: ITruncateOptions = {}) {
  const { omission = `...`, length: truncateAt = 20, separator } = options
  const maximumLength = truncateAt - omission.length
  const stop = separator
    ? text.lastIndexOf(separator, maximumLength) || maximumLength
    : maximumLength
  if (text.length > maximumLength) {
    return `${text.substring(0, stop)}${omission}`
  } else {
    return `${text.substring(0, stop)}`
  }
}

export function formatPrice(
  price: number,
  opts: { isPennies?: boolean; currency?: "" | "£" | "€" | "$"; compact?: boolean },
): string {
  return `${opts.currency ?? ""}${formatLargeNumber(
    opts.isPennies ? price / 100 : price,
    opts.compact,
  )}`
}

// Put commas in large numbers
function formatLargeNumber(val: number, compact?: boolean): string {
  function compactValue(value: number) {
    if (!compact) {
      return value.toFixed(2)
    } else if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(1)}B`
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    } else if (value >= 100) {
      return `${(value / 1000).toFixed(1)}K`
    } else {
      return value.toFixed(2)
    }
  }

  return compactValue(val).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export function totalPrice(record: {
  items: { price_pennies: number | null; quantity: number | null }[]
}) {
  return record.items.reduce(
    (total, item) => total + (item.price_pennies || 0) * (item.quantity || 1),
    0,
  )
}

export function currencyNameToSymbol(currency_name: string) {
  return _currencyNameToSymbol(currency_name)
}

export function generateToken() {
  return uuid.v4()
}

const baseApiUrl = process.env.API_URL

export function apiUrl(path?: string) {
  if (!baseApiUrl) {
    throw new Error("missing env variable API_URL")
  }

  if (!path) {
    return baseApiUrl
  } else if (path.startsWith("http")) {
    return path
  } else {
    return joinUrl(baseApiUrl, path)
  }
}

export function joinUrl(base: string, path: string) {
  const leadingSlash = path.startsWith("/")
  const trailingSlash = base.endsWith("/")

  if (leadingSlash && trailingSlash) {
    return `${base.slice(1)}${path}`
  } else if (leadingSlash || trailingSlash) {
    return `${base}${path}`
  } else {
    return `${base}/${path}`
  }
}

export function generateTempId() {
  return `$temp-${Math.random()}`
}

export function isTempId(id: string) {
  return id.startsWith(`$temp-`)
}

export function compare<T>(a: T, b: T) {
  return a < b ? -1 : a > b ? 1 : 0
}

export function uniq<T>(array: T[]) {
  return Array.from(new Set(array))
}

export type PropsFor<T> = T extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[T]
  : T extends (props: infer U) => JSX.Element
  ? U
  : never

export type Merge<T, U> = Pick<T, Exclude<keyof T, keyof U>> & U

export function calculateMarginFromPrices(basePrice: number, salePrice: number) {
  // Protect against divide by zero
  if (salePrice === 0) {
    return 0
  }
  return ((salePrice - basePrice) / salePrice) * 100
}

export function calculatePriceFromMargin(basePrice: number, margin: number) {
  return basePrice / ((100 - margin) / 100)
}

export function objectKeysAreAllEmpty(obj: any) {
  return Object.keys(obj).every(key => isBlank(obj[key]))
}

export function proRataCurrentTarget(target: number) {
  if (!target) {
    return 0
  }
  const currentDaysIntoMonth = new Date().getDate()
  const daysInMonth = dateFns.endOfMonth(new Date()).getDate()
  return (target / daysInMonth) * currentDaysIntoMonth
}

/** Returns pro rata % up or down as 25 or -25 */
export function proRataPctInt(target: number, salesToDate: number) {
  if (!target || !salesToDate) {
    return 0
  }
  const prt = proRataCurrentTarget(target)
  return (salesToDate / prt - 1) * 100
}

export function analysisCodesValue(analysisCodes: IAnalysisCodes, key: string) {
  const code = analysisCodes.find(code => code.key === key || code.name === key)
  return code?.value ?? ""
}

export function formatPercentage(value: number | null) {
  return value ? `${value.toFixed(2)}%` : null
}

export async function isInternalOrder(sourceBranchId: string, targetBranchId: string) {
  const branches = await window.skeem.fetch2<IRecord<"branches", "is_fortus_ew_branch">[]>({
    model: {
      name: "branches",
      attributes: ["is_fortus_ew_branch"],
      filter: {
        matches: {
          id: [sourceBranchId, targetBranchId],
        },
      },
    },
  })
  return branches.every(b => b.is_fortus_ew_branch)
}

export const popFields = [
  "analysisCodePoId",
  "analysisCodePoUser",
  "analysisCodePoWarehouse",
] as const
export const sopFields = [
  "analysisCodeSoId",
  "analysisCodeSoUser",
  "analysisCodeSoDispatchSource",
  "analysisCodeDeliveryMethod",
  "analysisCodeHikOppBranchDiscountFigure",
  "analysisCodeHikOppProjectRegistrationNumber",
] as const
export const cuFields = ["analysisCodeCuBranchCodeId", "analysisCodeCuBranchCode"] as const
export const analysisCodeFields = [...popFields, ...sopFields, ...cuFields]

export function getAnalysisCodeAttr(
  settings: Record<string, any>,
  attr: (typeof analysisCodeFields)[number],
  value: any,
  options?: { optional?: boolean },
) {
  const baseName = "analysis_code_"
  const num = settings[attr as keyof typeof settings]
  if (typeof num !== "number" || isNaN(Number(num))) {
    if (options?.optional) {
      return {}
    }
    throw new Error(`Invalid analysis code number for ${attr}`)
  }
  console.log("ANALYSIS CODE", num, baseName, value)
  return num ? { [`${baseName}${num}`]: value } : {}
}

export function isProductExported(product?: IRecord<"products", "sage_id">) {
  if (!product) {
    return true
  }
  return product.sage_id && !product.sage_id.toLowerCase().includes("generate")
}

export function handleCheckValidSellingPrice(
  price?: number | null,
  msp?: number | null,
  _stock_code?: string,
) {
  // errors.price_pennies = `Must be greater than minimum selling price ${msp}`

  if (!msp) {
    // console.log(`[Check min margin ${stock_code}] - no min set`, msp, price)
    return false
  }
  if (!msp || !price) {
    // console.log(`[Check min margin ${stock_code}] - no cost price or price`, msp, price)
    return false
  }
  if (price < msp) {
    return {
      msp: msp,
      price: msp,
      formatted: formatPrice(msp, { isPennies: true }),
      formattedPrice: formatPrice(price, { isPennies: true }),
    }
  }
  // console.log(`[Check min margin ${stock_code}] - valid`, msp, price)
  return null
}
export function handleCheckValidMargin(
  costPrice?: number | null,
  price?: number | null,
  minMargin?: number | null,
  _stock_code?: string,
) {
  if (!minMargin) {
    // console.log(`[Check min margin ${stock_code}] - no min set`, minMargin, costPrice, price)
    return false
  }
  if (!costPrice || !price) {
    // console.log(
    //   `[Check min margin ${stock_code}] - no cost price or price`,
    //   minMargin,
    //   costPrice,
    //   price,
    // )
    return false
  }
  const margin = calculateMarginFromPrice(costPrice, price)
  if (margin < minMargin) {
    const newPrice = calculatePriceFromMargin(costPrice, minMargin)
    // console.log(
    //   `[Check min margin ${stock_code}] - under required`,
    //   minMargin,
    //   margin,
    //   costPrice,
    //   price,
    //   newPrice,
    // )
    return { margin: minMargin, price: newPrice }
  }
  // console.log(`[Check min margin ${stock_code}] - valid`, minMargin, costPrice, price, margin)
  return null
}

export function calculateMarginFromPrice(
  costPricePennies?: number | null,
  quotedPricePennies?: number | null,
) {
  if (
    costPricePennies === quotedPricePennies ||
    !costPricePennies || // prevent divide by zero
    (!quotedPricePennies && quotedPricePennies !== 0)
  ) {
    return 0.0
  } else {
    const marginPercent =
      quotedPricePennies === 0 ? -100 : 100 - (costPricePennies / quotedPricePennies) * 100
    return Number((Math.floor(marginPercent * 100) / 100).toFixed(2))
  }
}

export function assert(condition: any, message: string): asserts condition {
  if (!condition) {
    throw new Error(message)
  }
}

// sort items so that child items of a bom always appear under the parent
export function sortItemsWithBOMs(items: (IItem | IDeletedItem)[]) {
  const notDeletedItems = items.filter(x => !isDeleted(x)) as IItem[]
  const baseItems = notDeletedItems.filter(x => !isDeleted(x) && x.type !== itemTypes.buildSetItem)
  for (let i = 0; i < baseItems.length; i++) {
    const item = baseItems[i]
    if (!isDeleted(item) && item.type === itemTypes.buildSet) {
      const buildSetItems = notDeletedItems.filter(
        x =>
          !isDeleted(x) &&
          x.type === itemTypes.buildSetItem &&
          x.build_set_uid === item.build_set_uid,
      )
      baseItems.splice(i + 1, 0, ...buildSetItems)
    }
  }
  return baseItems
}

export function calculateBuildQuantity(qty?: number | null, parentQty?: number | null) {
  if (!qty) {
    return 0
  }
  if (!parentQty) {
    return qty || 0
  }
  return Math.round(qty / parentQty)
}

/** {count} items are {message} | {items} */
export function errorMessageBuilder<T>(
  items: T[],
  message: string,
  fn: (item: T) => string,
  joiningPhrase = "are",
) {
  return [`${items.length} items ${joiningPhrase} ${message}`, `(${items.map(fn).join(", ")})`]
    .filter(Boolean)
    .join(" ")
}

export async function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
